import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { normalizeAircraftIdentifier, normalizeAirportCode } from "../routeUtils";

dayjs.extend(utc);

const WEEKDAY_CODES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MINUTES_PER_DAY = 24 * 60;
const OPERATION_ALIASES = {
    departure: new Set(["dep", "departure"]),
    arrival: new Set(["arr", "arrival"]),
};

function getFirstPresentValue(source, fields) {
    for (const field of fields) {
        if (source?.[field] !== undefined && source?.[field] !== null && source?.[field] !== "") {
            return source[field];
        }
    }
    return null;
}

function normalizeCurfewRuleCollection(value) {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }
    if (typeof value === "object") {
        return [value];
    }
    return [];
}

function normalizeOperation(value) {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) {
        return "";
    }
    if (OPERATION_ALIASES.departure.has(normalized)) {
        return "departure";
    }
    if (OPERATION_ALIASES.arrival.has(normalized)) {
        return "arrival";
    }
    return normalized;
}

function isRuleOperationMatch(rule, operation) {
    const expectedOperation = normalizeOperation(operation);
    const ruleOperation = normalizeOperation(rule?.operation || rule?.type_for);
    return !ruleOperation || ruleOperation === expectedOperation;
}

export function getAirportConstraint(airportConstraintsMap, airportCode) {
    const normalizedAirport = normalizeAirportCode(airportCode);
    if (!airportConstraintsMap || !normalizedAirport) {
        return null;
    }
    // 这里保留原始码回退：ScheduleDraft 消费的约束表来源不止一处，未必都以归一化后的码为键
    if (airportConstraintsMap instanceof Map) {
        return airportConstraintsMap.get(airportCode) || airportConstraintsMap.get(normalizedAirport) || null;
    }
    if (typeof airportConstraintsMap === "object") {
        return airportConstraintsMap[airportCode] || airportConstraintsMap[normalizedAirport] || null;
    }
    return null;
}

export function getCurfewRules(constraint, operation) {
    if (!constraint) {
        return [];
    }

    const normalizedOperation = normalizeOperation(operation);
    const ruleFields = normalizedOperation === "departure"
        ? ["dep_curfew_rules", "departure_curfew_rules", "dep_curfew_windows", "departure_curfew_windows"]
        : ["arr_curfew_rules", "arrival_curfew_rules", "arr_curfew_windows", "arrival_curfew_windows"];
    const rules = ruleFields.flatMap((field) => normalizeCurfewRuleCollection(constraint[field]));

    normalizeCurfewRuleCollection(constraint.curfew_rules).forEach((rule) => {
        if (isRuleOperationMatch(rule, normalizedOperation)) {
            rules.push(rule);
        }
    });
    normalizeCurfewRuleCollection(constraint.curfew_windows).forEach((rule) => {
        if (isRuleOperationMatch(rule, normalizedOperation)) {
            rules.push(rule);
        }
    });

    const start = getFirstPresentValue(constraint, normalizedOperation === "departure"
        ? ["dep_curfew_start", "departure_curfew_start", "curfew_start"]
        : ["arr_curfew_start", "arrival_curfew_start", "curfew_start"]);
    const end = getFirstPresentValue(constraint, normalizedOperation === "departure"
        ? ["dep_curfew_end", "departure_curfew_end", "curfew_end"]
        : ["arr_curfew_end", "arrival_curfew_end", "curfew_end"]);
    if (start && end) {
        rules.push({ start, end });
    }

    return rules;
}

export function getRuleWindow(rule) {
    return {
        start: rule?.start ?? rule?.start_time ?? rule?.from ?? rule?.curfew_start,
        end: rule?.end ?? rule?.end_time ?? rule?.to ?? rule?.curfew_end,
    };
}

export function parseTimeToMinutes(value) {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const match = String(value).trim().match(/^(\d{1,2})(?::(\d{1,2}))?$/);
    if (!match) {
        return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2] || 0);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null;
    }
    return hours * 60 + minutes;
}

function normalizeWeekday(value) {
    if (typeof value === "number") {
        return WEEKDAY_CODES[value % 7];
    }
    return String(value || "").slice(0, 3).toUpperCase();
}

function getRuleDateBounds(rule) {
    return {
        dateStart: rule?.date_start ?? rule?.valid_from,
        dateEnd: rule?.date_end ?? rule?.valid_to,
    };
}

export function getCurfewContractTime(timeUtc) {
    const parsed = dayjs.utc(timeUtc);
    if (!parsed.isValid()) {
        return null;
    }
    return parsed;
}

export function isCurfewRuleApplicable(rule, timeObj, aircraftReg) {
    const normalizedAircraftReg = normalizeAircraftIdentifier(aircraftReg);
    const exceptions = Array.isArray(rule?.exceptions) ? rule.exceptions : String(rule?.exceptions || "").split(/[,;\n]/).filter(Boolean);
    if (normalizedAircraftReg && exceptions.some((exception) => normalizeAircraftIdentifier(exception) === normalizedAircraftReg)) {
        return false;
    }

    const date = timeObj.format("YYYY-MM-DD");
    const ruleType = String(rule?.type || "daily").toLowerCase();
    const { dateStart, dateEnd } = getRuleDateBounds(rule);
    if (ruleType === "specific") {
        if (dateStart && dateEnd) {
            return date >= dateStart && date <= dateEnd;
        }
        if (dateStart) {
            return date === dateStart;
        }
        return false;
    }

    if (dateStart && date < dateStart) {
        return false;
    }
    if (dateEnd && date > dateEnd) {
        return false;
    }

    if (ruleType === "weekly") {
        const weekdays = Array.isArray(rule?.weekdays) ? rule.weekdays.map(normalizeWeekday) : [];
        return weekdays.length > 0 && weekdays.includes(WEEKDAY_CODES[timeObj.day()]);
    }

    return true;
}

function getRuleAnchorTime(timeObj, timeMinutes, startMinutes, endMinutes) {
    const isCrossNight = startMinutes > endMinutes;
    const anchorDay = isCrossNight && timeMinutes < endMinutes ? timeObj.subtract(1, "day") : timeObj;
    return anchorDay.startOf("day").add(startMinutes, "minute");
}

export function isTimeInCurfewRules(timeUtc, rules, aircraftReg, constraint = {}) {
    if (!timeUtc || !Array.isArray(rules) || !rules.length) {
        return { violated: false, rule: null };
    }

    const timeObj = getCurfewContractTime(timeUtc, constraint);
    if (!timeObj) {
        return { violated: false, rule: null };
    }

    for (const rule of rules) {
        const { start, end } = getRuleWindow(rule);
        const startMinutes = parseTimeToMinutes(start);
        const endMinutes = parseTimeToMinutes(end);
        if (startMinutes === null || endMinutes === null || startMinutes === endMinutes) {
            continue;
        }

        const timeMinutes = timeObj.hour() * 60 + timeObj.minute();
        const inWindow = startMinutes < endMinutes
            ? timeMinutes >= startMinutes && timeMinutes < endMinutes
            : timeMinutes >= startMinutes || timeMinutes < endMinutes;
        if (inWindow && isCurfewRuleApplicable(rule, getRuleAnchorTime(timeObj, timeMinutes, startMinutes, endMinutes), aircraftReg)) {
            return { violated: true, rule };
        }
    }

    return { violated: false, rule: null };
}

function isUsableUtcRange(timeWindow) {
    const start = timeWindow?.start ? dayjs.utc(timeWindow.start) : null;
    const end = timeWindow?.end ? dayjs.utc(timeWindow.end) : null;
    return start?.isValid() && end?.isValid() && end.isAfter(start) ? { start, end } : null;
}

function formatClockFromMinutes(value) {
    const minutes = Number(value);
    if (!Number.isFinite(minutes)) {
        return "--:--";
    }

    const minuteOfDay = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return `${String(Math.floor(minuteOfDay / 60)).padStart(2, "0")}:${String(minuteOfDay % 60).padStart(2, "0")}`;
}

function buildUtcProjectionDays(timeWindow) {
    const range = isUsableUtcRange(timeWindow);
    if (!range) {
        return [];
    }

    const days = [];
    let cursor = range.start.startOf("day").subtract(1, "day");
    const endDay = range.end.startOf("day").add(1, "day");
    while (cursor.isBefore(endDay) || cursor.isSame(endDay, "day")) {
        days.push(cursor);
        cursor = cursor.add(1, "day");
    }
    return days;
}

function buildRuleWindow(utcDay, startMinutes, endMinutes) {
    if (startMinutes === endMinutes) {
        return null;
    }

    const utcStart = utcDay.startOf("day").add(startMinutes, "minute");
    const utcEnd = utcDay.startOf("day").add(endMinutes + (startMinutes > endMinutes ? MINUTES_PER_DAY : 0), "minute");
    return { utcStart, utcEnd };
}

function clipToTimeWindow(startUtc, endUtc, timeWindow) {
    const range = isUsableUtcRange(timeWindow);
    if (!range || !startUtc?.isValid() || !endUtc?.isValid()) {
        return null;
    }

    const clippedStart = startUtc.isBefore(range.start) ? range.start : startUtc;
    const clippedEnd = endUtc.isAfter(range.end) ? range.end : endUtc;
    if (!clippedEnd.isAfter(clippedStart)) {
        return null;
    }
    return { start: clippedStart.toISOString(), end: clippedEnd.toISOString() };
}

function mergeProjectedWindow(existing, window) {
    if (!existing) {
        return window;
    }

    const operations = Array.from(new Set([...(existing.operations || [existing.operation]), window.operation].filter(Boolean)));
    return {
        ...existing,
        operations,
    };
}

function getOperationKey(window) {
    return (window.operations || [window.operation]).filter(Boolean).sort().join("|");
}

function mergeAdjacentProjectedWindows(windows) {
    return windows.reduce((merged, window) => {
        const previous = merged[merged.length - 1];
        if (
            previous
            && previous.airportCode === window.airportCode
            && previous.label === window.label
            && previous.end === window.start
            && getOperationKey(previous) === getOperationKey(window)
        ) {
            merged[merged.length - 1] = {
                ...previous,
                end: window.end,
            };
            return merged;
        }

        merged.push(window);
        return merged;
    }, []);
}

export function projectAirportCurfewWindows({ airportCode, constraint, operations = ["arrival", "departure"], aircraftReg, timeWindow }) {
    const normalizedAirportCode = normalizeAirportCode(airportCode || constraint?.iata_code || constraint?.airport_code || constraint?.code);
    const projectionDays = buildUtcProjectionDays(timeWindow);
    if (!normalizedAirportCode || !constraint || !projectionDays.length) {
        return [];
    }

    const windowsByKey = new Map();
    operations.forEach((operation) => {
        getCurfewRules(constraint, operation).forEach((rule, ruleIndex) => {
            const { start, end } = getRuleWindow(rule);
            const startMinutes = parseTimeToMinutes(start);
            const endMinutes = parseTimeToMinutes(end);
            if (startMinutes === null || endMinutes === null) {
                return;
            }

            const label = `${formatClockFromMinutes(startMinutes)}-${formatClockFromMinutes(endMinutes)}`;
            projectionDays.forEach((utcDay) => {
                const ruleWindow = buildRuleWindow(utcDay, startMinutes, endMinutes);
                if (!ruleWindow || !isCurfewRuleApplicable(rule, ruleWindow.utcStart, aircraftReg)) {
                    return;
                }

                const clipped = clipToTimeWindow(
                    ruleWindow.utcStart,
                    ruleWindow.utcEnd,
                    timeWindow,
                );
                if (!clipped) {
                    return;
                }

                const window = {
                    airportCode: normalizedAirportCode,
                    operation,
                    operations: [operation],
                    rule,
                    ruleIndex,
                    start: clipped.start,
                    end: clipped.end,
                    label,
                    windowStartLabel: formatClockFromMinutes(startMinutes),
                    windowEndLabel: formatClockFromMinutes(endMinutes),
                    utcDate: utcDay.format("YYYY-MM-DD"),
                };
                const key = `${normalizedAirportCode}:${clipped.start}:${clipped.end}:${label}`;
                windowsByKey.set(key, mergeProjectedWindow(windowsByKey.get(key), window));
            });
        });
    });

    return mergeAdjacentProjectedWindows(
        Array.from(windowsByKey.values()).sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime()),
    );
}
